import React, { useState, useRef } from 'react';
import InfoTooltip from './InfoTooltip';
import './TaggingPage.css';
import '../Editor.css';


const logConfigChange = async (pid, scenario, configField, oldValue, newValue, configState) => {
    try {
        await fetch('/api/log/config', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pid,
                scenario,
                config_field: configField,
                old_value: oldValue,
                new_value: newValue,
                config_state: configState
            })
        });
    } catch (error) {
        console.error('Error logging config change:', error);
    }
};

const ConfigSection = ({
                           config,
                           onConfigChange,
                           onGenerate,
                           loading,
                           scenario,
                           editorContent = '',
                           pid
                       }) => {
    const [dropdowns, setDropdowns] = useState({
        creativity: false,
        poeticElement: false,
        rhymeSchema: false,
    });
    // keep inspiration local while typing (no per-keystroke logging)
    const [inspDraft, setInspDraft] = useState(config.inspiration || '');

    // de-dupe for other config fields (keep your existing cache + logOnce)
    const lastLoggedRef = useRef({});
    const logOnce = async (field, oldVal, newVal, stateObj) => {
        if (!pid) return;

        const sig = `${String(oldVal)}→${String(newVal)}`;
        const now = Date.now();
        const last = lastLoggedRef.current[field];

        // cooldown 1000ms to absorb accidental duplicates
        if (last && last.sig === sig && (now - last.ts) < 1000) return;

        // await logConfigChange(pid, scenario, field, oldVal, newVal, stateObj);
        lastLoggedRef.current[field] = { sig, ts: now };
    };

    // count words (splitting on whitespace)
    const wordCount = editorContent.trim().split(/\s+/).filter(Boolean).length;
    // disable if co-write and less than 4 words, or if loading
    const isWriteDisabled = loading || (scenario === 'co-write' && wordCount < 4);

    const toggleDropdown = (key) =>
        setDropdowns((prev) => ({ ...prev, [key]: !prev[key] }));

    const handleCreativitySelect = async (level) => {
        const oldValue = config.creativity;
        if (pid && oldValue !== level) {
            const trimmedInsp = (inspDraft || '').trim();
            await logOnce(
                'creativity',
                oldValue,
                level,
                {
                    creativity: level,
                    poeticElement: config.poeticElement,
                    rhymeSchema: config.rhymeSchema,
                    numResponses: config.numResponses,
                    lineLimit: config.lineLimit,
                    inspiration: trimmedInsp,  // <-- live inspiration
                }
            );
        }
        onConfigChange({ creativity: level });
        toggleDropdown('creativity');
    };

    const handlePoeticElementSelect = async (el) => {
        const oldValue = config.poeticElement;
        if (pid && oldValue !== el) {
            const trimmedInsp = (inspDraft || '').trim();
            await logOnce(
                'poeticElement',
                oldValue,
                el,
                {
                    creativity: config.creativity,
                    poeticElement: el,
                    rhymeSchema: config.rhymeSchema,
                    numResponses: config.numResponses,
                    lineLimit: config.lineLimit,
                    inspiration: trimmedInsp,  // <-- live inspiration
                }
            );
        }
        onConfigChange({ poeticElement: el });
        toggleDropdown('poeticElement');
    };

    const handleRhymeSchemaSelect = async (schema) => {
        const oldValue = config.rhymeSchema;
        if (pid && oldValue !== schema) {
            const trimmedInsp = (inspDraft || '').trim();
            await logOnce(
                'rhymeSchema',
                oldValue,
                schema,
                {
                    creativity: config.creativity,
                    poeticElement: config.poeticElement,
                    rhymeSchema: schema,
                    numResponses: config.numResponses,
                    lineLimit: config.lineLimit,
                    inspiration: trimmedInsp,  // <-- live inspiration
                }
            );
        }
        onConfigChange({ rhymeSchema: schema });
        toggleDropdown('rhymeSchema');
    };

    const handleNumResponsesChange = async (e) => {
        const newValue = parseInt(e.target.value, 10);
        const oldValue = config.numResponses;
        if (pid && oldValue !== newValue) {
            const trimmedInsp = (inspDraft || '').trim();
            await logOnce(
                'numResponses',
                oldValue,
                newValue,
                {
                    creativity: config.creativity,
                    poeticElement: config.poeticElement,
                    rhymeSchema: config.rhymeSchema,
                    numResponses: newValue,
                    lineLimit: config.lineLimit,
                    inspiration: trimmedInsp,  // <-- live inspiration
                }
            );
        }
        onConfigChange({ numResponses: newValue });
    };

    const handleLineLimitChange = async (e) => {
        const newValue = parseInt(e.target.value, 10);
        const oldValue = config.lineLimit;
        if (pid && oldValue !== newValue) {
            const trimmedInsp = (inspDraft || '').trim();
            await logOnce(
                'lineLimit',
                oldValue,
                newValue,
                {
                    creativity: config.creativity,
                    poeticElement: config.poeticElement,
                    rhymeSchema: config.rhymeSchema,
                    numResponses: config.numResponses,
                    lineLimit: newValue,
                    inspiration: trimmedInsp,  // <-- live inspiration
                }
            );
        }
        onConfigChange({ lineLimit: newValue });
    };

    const handleInspirationChange = (e) => setInspDraft(e.target.value);


    const handleGenerateClick = async () => {
        const trimmed = (inspDraft || '').trim();

        // sync parent config BEFORE generating so backend sees the right value
        if (trimmed !== (config.inspiration || '')) {
            onConfigChange({ inspiration: trimmed });
        }

        if (pid && trimmed) {
            // direct call: single, canonical inspiration log on Generate
            await logConfigChange(
                pid,
                scenario,
                'inspiration',
                config.inspiration || '',
                trimmed,
                {
                    creativity: config.creativity,
                    poeticElement: config.poeticElement,
                    rhymeSchema: config.rhymeSchema,
                    numResponses: config.numResponses,
                    lineLimit: config.lineLimit,
                    inspiration: trimmed,
                }
            );
        }

        onGenerate?.();
    };



    const creativityLevels = { low: 'Low', medium: 'Medium', high: 'High' };
    const poeticElements = {
        none: 'None', simile: 'Simile', metaphor: 'Metaphor',
        personification: 'Personification', hyperbole: 'Hyperbole',
        irony: 'Irony', alliteration: 'Alliteration',
        onomatopoeia: 'Onomatopoeia', oxymoron: 'Oxymoron',
        paradox: 'Paradox',
    };
    const rhymeSchemas = {
        none: 'None', free_verse: 'Free verse', aaaa: 'AAAA',
        aabb: 'AABB', abab: 'ABAB', abba: 'ABBA', haiku: 'Haiku',
    };

    // slider bounds
    const lineMin = scenario === 'co-write' ? 1 : 2;
    const lineMax = scenario === 'co-write' ? 4 : 12;

    return (
        <div className="config-section">
            <div className="config-header">
                <h2 className="section-title">
                    {scenario === 'co-write'
                        ? 'Co-write Configuration'
                        : 'Command Configuration'}
                </h2>
                <button
                    className="generate-button"
                    onClick={handleGenerateClick}
                    disabled={scenario === 'co-write' ? isWriteDisabled : loading}
                >
                    {loading
                        ? scenario === 'co-write'
                            ? 'Completing…'
                            : 'Generating…'
                        : scenario === 'co-write'
                            ? 'Write'
                            : 'Generate'}
                </button>
            </div>

            {/* Creativity – shown in all modes */}
            <div className="config-item">
                <div className="config-label-wrapper">
                    <span className="config-label">Creativity</span>
                    <InfoTooltip>
                        Adjust how daring the AI’s wording is.<br/>
                        <strong>Low</strong>: Safe phrasing.<br/>
                        <strong>High</strong>: More novel words.
                    </InfoTooltip>
                </div>
                <div className="dropdown-container">
                    <div
                        className="dropdown-select"
                        onClick={() => toggleDropdown('creativity')}
                    >
                        {creativityLevels[config.creativity]}
                        <span>▼</span>
                    </div>
                    {dropdowns.creativity && (
                        <div className="dropdown-options">
                            {Object.entries(creativityLevels).map(([key, label]) => (
                                <div
                                    key={key}
                                    className="dropdown-option"
                                    onClick={() => handleCreativitySelect(key)}
                                >
                                    {label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {scenario === 'co-write' ? (
                /* Co-write: only line limit */
                <div className="config-item">
                    <div className="config-label-wrapper">
                        <span className="config-label">Line Limit</span>
                        <InfoTooltip>
                            Number of lines AI will auto-complete (max 4).
                        </InfoTooltip>
                    </div>
                    <div className="slider-container">
                        <div className="slider-label">
                            <span>{lineMin}</span>
                            <span>{lineMax} Lines</span>
                        </div>
                        <div className="slider-with-value">
                            <input
                                type="range"
                                min="1"
                                max="4"
                                value={config.lineLimit}
                                onChange={handleLineLimitChange}
                                className="slider"
                            />
                            <div className="slider-value">{config.lineLimit}</div>
                        </div>
                    </div>
                </div>
            ) : (
                /* Curator & AI-Only: full panel */
                <>
                    {/* Poetic Element */}
                    <div className="config-item">
                        <div className="config-label-wrapper">
                            <span className="config-label">Poetic Element</span>
                            <InfoTooltip>
                                Add figurative language (simile, metaphor…) to enrich your poem.
                            </InfoTooltip>
                        </div>
                        <div className="dropdown-container">
                            <div
                                className="dropdown-select"
                                onClick={() => toggleDropdown('poeticElement')}
                            >
                                {poeticElements[config.poeticElement]}
                                <span>▼</span>
                            </div>
                            {dropdowns.poeticElement && (
                                <div className="dropdown-options">
                                    {Object.entries(poeticElements).map(([key, label]) => (
                                        <div
                                            key={key}
                                            className="dropdown-option"
                                            onClick={() => handlePoeticElementSelect(key)}
                                        >
                                            {label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Rhyme Schema */}
                    <div className="config-item">
                        <div className="config-label-wrapper">
                            <span className="config-label">Rhyme Schema</span>
                            <InfoTooltip>
                                Choose AABB, ABAB, free verse, etc.
                            </InfoTooltip>
                        </div>
                        <div className="dropdown-container">
                            <div
                                className="dropdown-select"
                                onClick={() => toggleDropdown('rhymeSchema')}
                            >
                                {rhymeSchemas[config.rhymeSchema]}
                                <span>▼</span>
                            </div>
                            {dropdowns.rhymeSchema && (
                                <div className="dropdown-options">
                                    {Object.entries(rhymeSchemas).map(([key, label]) => (
                                        <div
                                            key={key}
                                            className="dropdown-option"
                                            onClick={() => handleRhymeSchemaSelect(key)}
                                        >
                                            {label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Inspiration */}
                    <div className="config-item">
                        <div className="config-label-wrapper">
                            <span className="config-label">Source of Inspiration</span>
                            <InfoTooltip>
                                Provide a word or phrase to guide the theme.
                            </InfoTooltip>
                        </div>
                        <input
                            type="text"
                            className="inspiration-input"
                            value={inspDraft}
                            onChange={handleInspirationChange}
                            placeholder="Enter inspiration…"
                        />
                    </div>

                    {/* Responses */}
                    <div className="config-item">
                        <div className="config-label-wrapper">
                            <span className="config-label">Responses</span>
                            <InfoTooltip>
                                How many different suggestions to generate.
                            </InfoTooltip>
                        </div>
                        <div className="slider-container">
                            <div className="slider-label">
                                <span>1</span>
                                <span>4 Responses</span>
                            </div>
                            <div className="slider-with-value">
                                <input
                                    type="range"
                                    min="1"
                                    max="4"
                                    value={config.numResponses}
                                    onChange={handleNumResponsesChange}
                                    className="slider"
                                />
                                <div className="slider-value">{config.numResponses}</div>
                            </div>
                        </div>
                    </div>

                    {/* Line Limit under Responses */}
                    <div className="config-item">
                        <div className="config-label-wrapper">
                            <span className="config-label">Line Limit</span>
                            <InfoTooltip>
                                Number of lines in each suggestion (max 12).
                            </InfoTooltip>
                        </div>
                        <div className="slider-container">
                            <div className="slider-label">
                                <span>2</span>
                                <span>12 Lines</span>
                            </div>
                            <div className="slider-with-value">
                                <input
                                    type="range"
                                    min="2"
                                    max="12"
                                    value={config.lineLimit}
                                    onChange={handleLineLimitChange}
                                    className="slider"
                                />
                                <div className="slider-value">{config.lineLimit}</div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ConfigSection;
